import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const WarehouseSnapshotReport = ({ snapData = [], customers = [], theme }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [summaryStats, setSummaryStats] = useState({});

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'lane_id', label: 'Lane ID' },
        { key: 'product_id', label: 'Product ID' },
        { key: 'product_description', label: 'Description' },
        { key: 'quantity', label: 'Pallet Quantity' },
        { key: 'items_per_pallet', label: 'Items Per Pallet' },
        { key: 'total_items', label: 'Total Items' },
    ];

    // Helper function to get company name from customer_id
    const getCompanyName = (customerId) => {
        const customer = customers.find(c => c.id === customerId);
        return customer ? customer.company : `Customer ${customerId}`;
    };

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true);
        logger.log("Snapshot data received:", snapData);
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
            } else {
                logger.log("Invalid password.");
                showAlert({
                    message: 'Invalid password. Access denied.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            }
        } catch (error) {
            logger.error("Error validating password:", error);
            showAlert({
                message: 'An error occurred while validating password.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }
    };

    const showData = () => {
        logger.log("Warehouse Snapshot Data:", snapData);
        
        if (snapData && snapData.summary) {
            // Since we only get summary data now, just set the summary stats
            setSummaryStats(snapData.summary);
            setTableData([]); // No table data anymore
        } else {
            setTableData([]);
            setSummaryStats({});
        }
        setOpenRequestPassword(false);
    };

    const calculateSummaryStats = (data) => {
        // This function is no longer needed as summary comes from backend
        // but keeping it for compatibility
        if (summaryStats && Object.keys(summaryStats).length > 0) {
            return; // Use the summary from backend
        }
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '0';
        return parseFloat(value).toFixed(0);
    };

    return (
        <div className="container-fluid p-0">
            <RequestPassword
                theme={theme}
                isOpen={openRequestPassword}
                setIsOpen={setOpenRequestPassword}
                returnData={handlePasswordSubmission}
            />

            {summaryStats && Object.keys(summaryStats).length > 0 ? (
                <div>
                    {/* Summary Stats */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <h5 className="mb-0">Warehouse Snapshot Summary - {getCompanyName(summaryStats.customer_id)}</h5>
                                </div>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <strong>Total Pallets Onsite:</strong> {formatNumber(summaryStats.total_pallets_onsite)}
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Total Different Products:</strong> {formatNumber(summaryStats.total_different_products)}
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Total Products Onsite:</strong> {formatNumber(summaryStats.total_products_onsite)}
                                        </div>
                                    </div>
                                    <div className="row mt-2">
                                        <div className="col-md-6">
                                            <strong>Customer:</strong> {getCompanyName(summaryStats.customer_id)}
                                        </div>
                                        <div className="col-md-6">
                                            <strong>Snapshot Date:</strong> {new Date().toLocaleDateString('en-GB')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Note about simplified view */}
                    <div className="row">
                        <div className="col-12">
                            <div className={`alert ${isLightTheme ? 'alert-success' : 'alert-dark'} text-center`}>
                                <h5>📊 Warehouse Snapshot Complete</h5>
                                <p>This summary shows the current stock levels for the selected customer.</p>
                                <small className="text-muted">
                                    Summary includes all pallets with products assigned and total product counts.
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row">
                    <div className="col-12">
                        <div className={`alert ${isLightTheme ? 'alert-warning' : 'alert-dark'} text-center`}>
                            <h4>🔐 Access Required</h4>
                            <p>Please enter your password to view the warehouse snapshot.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WarehouseSnapshotReport;
